<template>
  <div>
    <div class="row mb-2 pe-4">
      <div class="col-lg-8 col-md-7 col-7 d-flex align-items-center">
        <div class="color-common">
          <h3 class="title-page mb-0 ps-3">Import menu set (JSON)</h3>
        </div>
      </div>
    </div>
    <div class="page-header align-items-start min-vh-100">
      <span class="mask bg-white"></span>
      <div class="container">
        <div class="row">
          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-3">
            <div class="text-sm text-secondary mb-4">
              Rebuilds a menu set exported from another merchant: categories, dishes, prices,
              modifiers and schedules. It always creates a new menu set — nothing you already have
              is changed. Item numbers are kept when free here and renumbered when taken.
            </div>

            <div class="d-flex mt-4 s-menuset align-items-center">
              <div class="me-3">File</div>
              <material-input
                hidden
                type="file"
                id="file_import_json"
                name="file_import_json"
                @change="onFileChange($event)"
                accept=".json"
              />
              <material-button
                @click="chooseFile()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Choose file
              </material-button>
              <div>{{ fileName }}</div>
            </div>
            <div class="d-flex mt-2 s-menuset" v-if="fileError">
              <div class="me-3"></div>
              <div class="is-invalid">{{ fileError }}</div>
            </div>

            <div class="d-flex mt-4 s-menuset align-items-center">
              <div class="me-3">New name</div>
              <material-input
                v-model="name"
                type="text"
                placeholder="Name for the new menu set"
                class="w-100 mw-500px"
              />
            </div>
            <div class="d-flex mt-2 s-menuset" v-if="nameError">
              <div class="me-3"></div>
              <div class="is-invalid">{{ nameError }}</div>
            </div>

            <div class="d-flex mt-4 s-menuset align-items-center">
              <div class="me-3">Sales channels</div>
              <Multiselect
                v-model="branchIds"
                :options="branches"
                mode="multiple"
                placeholder="Assign to channels (optional)"
                :hideSelected="false"
                :closeOnSelect="false"
              />
            </div>

            <div class="d-flex mt-4 s-menuset align-items-center">
              <div class="me-3">Pictures</div>
              <material-input
                hidden
                type="file"
                id="file_images_zip"
                name="file_images_zip"
                @change="onImagesChange($event)"
                accept=".zip"
              />
              <material-button
                @click="chooseImages()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Choose .zip
              </material-button>
              <div>{{ imagesName }}</div>
            </div>
            <div class="d-flex mt-2 s-menuset">
              <div class="me-3"></div>
              <div class="text-sm text-secondary">
                Optional — the images .zip downloaded next to the JSON. Leave it empty when both
                merchants are on this same server: the pictures are already here and get copied.
              </div>
            </div>
            <div class="d-flex mt-2 s-menuset" v-if="imagesError">
              <div class="me-3"></div>
              <div class="is-invalid">{{ imagesError }}</div>
            </div>

            <div class="mt-5 d-flex flex-wrap align-items-center">
              <material-button
                @click="preview()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Preview
              </material-button>
              <!-- Import does not wait for a preview. The server validates the file again and
                   writes in one transaction, so gating the button only made it look broken. -->
              <material-button
                @click="confirmImport()"
                class="btn btn-sm btn-background mw-185px"
              >
                Import
              </material-button>
              <span class="text-sm text-secondary ms-3">Preview is optional — it only shows what would be created.</span>
            </div>
          </div>

          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-5" v-if="previewResult">
            <h4 class="title-page">What will be created</h4>
            <div class="text-sm text-secondary mb-3" v-if="previewResult.source">
              From merchant
              <b>{{ previewResult.source.merchant_code || "-" }}</b>, menu set
              <b>{{ previewResult.source.menuset_name || "-" }}</b>
            </div>
            <table class="table table-sm w-auto">
              <tbody>
                <tr v-for="(value, key) in previewResult.will_create" :key="key">
                  <td class="text-sm ps-0 pe-4">{{ label(key) }}</td>
                  <td class="text-sm text-end">{{ value }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="previewResult.warnings && previewResult.warnings.length" class="mt-3">
              <div
                v-for="(warning, index) in previewResult.warnings"
                :key="index"
                class="text-sm text-warning"
              >
                {{ warning }}
              </div>
            </div>
          </div>

          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-5" v-if="importResult">
            <h4 class="title-page">Imported</h4>
            <div class="text-sm mb-3">
              Menu set <b>{{ importResult.menuset.name }}</b> created.
            </div>
            <table class="table table-sm w-auto">
              <tbody>
                <tr v-for="(value, key) in importResult.created" :key="key">
                  <td class="text-sm ps-0 pe-4">{{ label(key) }}</td>
                  <td class="text-sm text-end">{{ value }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="importResult.warnings && importResult.warnings.length" class="mt-3">
              <div
                v-for="(warning, index) in importResult.warnings"
                :key="index"
                class="text-sm text-warning"
              >
                {{ warning }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="position-fixed top-1 end-1 z-index-2 message-success">
    <material-snackbar
      v-if="snackbar === 'danger' || snackbar === 'success'"
      title
      date
      :description="message"
      :icon="{ component: 'campaign', color: 'white' }"
      :color="snackbar"
      :close-handler="closeSnackbar"
    />
  </div>
  <material-loading :active="loaderActive" />
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialInput from "@/components/MaterialInput.vue";
import Multiselect from "@vueform/multiselect";
import MenusetService from "../../services/product/menuset.service";
import $ from "jquery";
import { scrollToFirstInvalid } from "@/config/utils";

export default {
  name: "import-menu-set-json",
  components: {
    Multiselect,
    MaterialSnackbar,
    MaterialLoading,
    MaterialButton,
    MaterialInput,
  },
  data() {
    return {
      file: null,
      fileName: "No file chosen",
      fileError: "",
      name: "",
      nameError: "",
      branchIds: [],
      branches: [],
      images: null,
      imagesName: "No file chosen",
      imagesError: "",
      previewResult: null,
      importResult: null,
      snackbar: null,
      message: "",
      loaderActive: false,
    };
  },
  created() {
    this.getBranches();
  },
  methods: {
    getBranches() {
      MenusetService.getBranchs({}).then(
        (response) => {
          if (response.data.result.isSuccess) {
            // get-branchs answers { branchSelect: [{ value, label }] }, already in the shape
            // Multiselect wants - not a bare array of branch rows.
            this.branches = response.data.data?.branchSelect ?? [];
          }
        },
        () => {
          // The channel list is optional - the import runs without it.
        }
      );
    },

    chooseFile() {
      $("#file_import_json").click();
    },

    chooseImages() {
      $("#file_images_zip").click();
    },

    onImagesChange(event) {
      const file = event.target.files[0];

      this.imagesError = "";

      if (!file) {
        this.images = null;
        this.imagesName = "No file chosen";
        return;
      }

      if (!file.name.toLowerCase().endsWith(".zip")) {
        this.images = null;
        this.imagesName = "No file chosen";
        this.imagesError = "Only a .zip archive of pictures can be uploaded here.";
        return;
      }

      this.images = file;
      this.imagesName = file.name;
    },

    onFileChange(event) {
      const file = event.target.files[0];

      this.previewResult = null;
      this.importResult = null;
      this.fileError = "";

      if (!file) {
        this.file = null;
        this.fileName = "No file chosen";
        return;
      }

      if (!file.name.toLowerCase().endsWith(".json")) {
        this.file = null;
        this.fileName = "No file chosen";
        this.fileError = "Only .json files exported from a menu set can be imported.";
        return;
      }

      this.file = file;
      this.fileName = file.name;

      // A sensible default the merchant can overwrite, taken from the file itself.
      if (!this.name) {
        this.readSourceName(file);
      }
    },

    readSourceName(file) {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const payload = JSON.parse(reader.result);
          const sourceName = payload && payload.source ? payload.source.menuset_name : "";

          if (sourceName) {
            this.name = sourceName + " (imported)";
          }
        } catch (e) {
          // Left to the server to report - it is the one that decides what a valid file is.
        }
      };

      reader.readAsText(file);
    },

    formData() {
      const form = new FormData();

      form.append("file_import", this.file);
      form.append("name", this.name);

      if (this.images) {
        form.append("file_images", this.images);
      }

      (this.branchIds || []).forEach((id) => form.append("branch_ids[]", id));

      return form;
    },

    valid() {
      this.fileError = this.file ? "" : "Choose a .json file to import.";
      this.nameError = this.name && this.name.trim() ? "" : "The field is required.";

      if (this.fileError || this.nameError) {
        scrollToFirstInvalid();
      }

      return !this.fileError && !this.nameError;
    },

    preview() {
      if (!this.valid()) {
        return;
      }

      this.showLoader();
      this.importResult = null;

      MenusetService.importJsonPreview(this.formData()).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.previewResult = response.data.data;
          } else {
            this.previewResult = null;
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.previewResult = null;
          this.snackbar = "danger";
          this.message = this.errorMessage(error);
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    confirmImport() {
      if (!this.valid()) {
        return;
      }

      this.showLoader();

      MenusetService.importJson(this.formData()).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.importResult = response.data.data;
            this.previewResult = null;
            this.snackbar = "success";
            this.message = "Menu set imported.";
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
          }
          this.hideLoader();
          this.setTimeOutNotification();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = this.errorMessage(error);
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    errorMessage(error) {
      const result = error && error.response && error.response.data ? error.response.data.result : null;

      if (result && result.message) {
        return result.message;
      }

      // No response at all: the request timed out, the connection dropped, or the server died
      // mid-import. Saying that beats surfacing whatever TypeError the axios layer threw.
      if (error && !error.response) {
        return "The server did not answer — the import may have timed out or the file may be too large. Check the API log before retrying.";
      }

      return String(error);
    },

    /** minty_display_category_item -> "Category item" */
    label(key) {
      const text = String(key).replace(/_/g, " ");

      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 5000);
    },
  },
};
</script>

<style scoped>
.page-header .container {
  color: #222 !important;
}

.s-menuset .multiselect {
  margin: 0 !important;
  max-width: 500px !important;
}

.s-menuset > div:first-child {
  min-width: 120px;
}

.mw-500px {
  max-width: 500px;
}

</style>
